import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.roofing;

export const articleSignsNeedNewRoof: Article = {
  slug: "10-signs-you-need-a-new-roof",
  title: "How to Tell If Your Roof Is Actually Failing",
  description: "Curling shingles, granules in the gutter, light through the attic, learn which warning signs mean a repair can buy you time and which mean the clock has already run out.",
  category: "Roofing",
  tags: ["Roofing", "Home Inspection", "Maintenance Checklist", "DIY Tips"],
  createdAt: "June 19, 2026",
  updatedAt: "June 29, 2026",
  lastVerified: "June 29, 2026",
  readTime: "8 min read",
  keyTakeaway: "Not every roof problem needs a full replacement. The signs that matter most are granule loss in the gutters, visible daylight from the attic, and a sagging roofline, any one of those is structural. Curling or cracked shingles across a large area means the material is spent. Isolated damage on a younger roof is usually a repair job.",
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "National Roofing Contractors Association (NRCA) - Homeowner Roofing Guides",
      url: "https://www.nrca.net"
    },
    {
      name: "Federal Emergency Management Agency (FEMA) - Homeowner Inspection Standards",
      url: "https://www.fema.gov"
    }
  ],
  keyMetrics: [
    { label: "Inspect Every", value: "2x Per Year" },
    { label: "Asphalt Shingle Lifespan", value: `${p.lifespanYears.asphalt.min}-${p.lifespanYears.asphalt.max} Years` },
    { label: "Repair vs. Replace Threshold", value: "~12 Years" },
    { label: "Structural Danger Sign", value: "Sagging Deck" }
  ],
  faqs: [
    {
      question: "Can I just patch a single leak, or do I need a complete roof replacement?",
      answer: "If your roof is relatively new (under 12 years old) and the damage is localized, like a rusted piece of flashing or a single shingle blown off in a storm, a patch is a cost-effective fix. However, if the shingles are old, dry, and curling across the entire slope, patching one leak is just delaying the inevitable. Plan for a full replacement."
    },
    {
      question: "Is it safe to install a second layer of shingles over my old roof to save money?",
      answer: "While building codes in some areas allow a second layer of shingles, it is rarely a good idea. A second layer traps heat, which shortens the lifespan of the new shingles. It also prevents the contractor from inspecting and repairing rotted decking underneath, and adds significant weight to your home's rafters."
    }
  ],
  content: `
    <p>
      Clean your gutters in late autumn and you'll scoop out leaves, twigs, and something else: a thick, gritty layer of dark mineral granules settled at the bottom of the channel. These aren't dirt. They're the UV-protective coating that keeps your shingles from baking apart in summer heat. When a roof starts losing them in volume, the clock is already running.
    </p>
    <p>
      Most homeowners don't catch roof problems until water shows up on a ceiling, by which point the damage has usually been building for months. The signs below show up earlier. Some of them you can spot from the ground in five minutes. Others require a flashlight and a trip to the attic.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Inspect twice a year, spring and fall, plus after any storm with hail or winds above 50 mph. Granules in the gutters, daylight visible through the attic deck, and a sagging roofline are the three signs that move this from "monitor it" to "call a contractor." A roof under 12 years old with isolated damage is a repair. One over 20 with widespread shingle failure is a replacement.
      </p>
    </div>

    <h2>This article covers:</h2>
    <ul>
      <li>The difference between signs that mean "keep an eye on it" and signs that mean "it's already failing"</li>
      <li>What to look for in the attic before you ever set foot on the roof</li>
      <li>Why a second layer of shingles is usually the wrong call</li>
      <li>How to decide between a patch repair and a full replacement</li>
    </ul>

    <h2>The Signs You Can Spot From the Ground</h2>
    <p>
      You don't need to climb anything to catch most early roof problems. A pair of binoculars and a walk around the perimeter will cover most of it.
    </p>
    <p>
      <strong>Curling or cupping shingles</strong> are the most visible sign of age. When shingles curl upward at the edges (cupping) or buckle in the middle (clawing), they've lost their flexibility from years of heat and UV exposure (refer to our <a href="/articles/roof-lifespan-by-material/" class="text-[var(--accent)] hover:underline font-semibold">roof lifespan by material guide</a> for details). Water pools in those deformations instead of shedding off. If it's happening across a full slope rather than a few isolated shingles, the material is spent.
    </p>
    <p>
      <strong>Missing shingles</strong> after a storm can mean the nailing pattern was insufficient or the shingles were brittle enough to lift. One or two missing on a younger roof is a repair. A cluster missing on a roof over 15 years old is a sign the adhesive strip along the entire surface is failing.
    </p>
    <p>
      <strong>Sagging anywhere along the roofline</strong> is the most urgent sign on this list. A straight roofline should be perfectly level from ridge to eave. Any dip or wave in the middle of a span means the decking or structural rafters underneath have been compromised, likely from prolonged moisture exposure. This is not a patch job.
    </p>

    <!--COMPONENT:warning-note|label="Attic Mold Warning"-->
    High moisture from a slow roof leak breeds mold in attic insulation and on the decking before it ever shows up on your ceiling. The EPA notes that mold covering more than 10 square feet requires professional remediation, and attic mold often spreads far wider than what's visible. Address leaks as soon as you find them; the longer you wait, the more material needs to come out.
    <!--ENDOFCOMPONENT-->

    <h2>The Attic Is the Real Inspection Point</h2>
    <p>
      Grab a flashlight and check these on a sunny day. Kill the attic lights and let your eyes adjust first.
    </p>
    <ul>
      <li><strong>Daylight through the roof deck:</strong> Any pinhole of natural light means the decking is breached. This is structural damage, not a surface problem.</li>
      <li><strong>Water stains or dark rings:</strong> Look around chimneys, plumbing vents, and skylight curbs. Stains that are dry now were wet recently.</li>
      <li><strong>Wet or compressed insulation:</strong> Feel the fiberglass batts under any suspect areas. Damp insulation loses its R-value and holds moisture against the decking for weeks after a rain event.</li>
      <li><strong>Soft rafters:</strong> Press a screwdriver against any dark spots on the rafter wood. If it sinks, the wood is rotted and will need replacement before a new roof can go on.</li>
    </ul>

    <h2>Granules in the Gutter: How Much Is Too Much?</h2>
    <p>
      New shingles shed some granules during the first few weeks, that's normal. What's not normal is a thick, consistent buildup every time you clean the gutters, especially on a roof that's been installed for years.
    </p>
    <p>
      To gauge severity: if you can scoop a full handful of granules out of the gutter after a single rain, check the shingles themselves. You'll see bare, dark asphalt patches where the coating has worn through. Those bare spots absorb heat instead of reflecting it, accelerating the deterioration of the surrounding shingles.
    </p>
    <p>
      Widespread granule loss on a roof over 15 years old is not a patch situation. The shingles have exceeded their protective lifespan across the whole surface.
    </p>

    <h2>Repair vs. Replace: The Practical Decision</h2>
    <p>
      The age of the roof and the extent of the damage determine which path makes financial sense.
    </p>
    <p>
      A roof under 12 years old with damage limited to a specific area, storm-blown shingles, a failed flashing around a chimney, a single soft spot, is a repair candidate. The surrounding material is still in good condition and has years of service left.
    </p>
    <p>
      A roof over 20 years old showing granule loss, widespread curling, or any structural sign is a replacement. Patching individual problems on aging material is like replacing one tire on a set with worn-out tread: you'll be back at the shop in six months for another. A licensed contractor can give you a condition report after a physical inspection, get at least three before committing to either path (to check if your insurance will cover the work, read our guide on <a href="/articles/does-homeowners-insurance-cover-roof-replacement/" class="text-[var(--accent)] hover:underline font-semibold">does homeowners insurance cover roof replacement?</a>).
    </p>
    <p>
      Ready to estimate what a full replacement would cost for your home's footprint? Use our <a href="/calculators/roofing/" class="text-[var(--accent)] hover:underline font-semibold">Roof Replacement Cost Calculator</a> to get installed price ranges by material and square footage.
    </p>
  `
};